// frontend/src/features/ups/ui/companyActionClasses.js
/**
 * Company/admin action class variants.
 *
 * This helper owns visual action styling only.
 * Calling components still own behavior:
 * - onClick handlers
 * - disabled/loading state
 * - labels
 * - icons
 * - aria/title attributes
 * - menu open/close logic
 *
 * The system separates:
 * - intent: what the action means visually
 * - size/context: where the action appears
 *
 * Red/green/amber are universal operational action semantics,
 * not UPS-brand-specific theme colors.
 */

// Shared button shell for normal, compact, and icon actions.
const BASE =
  'inline-flex items-center justify-center rounded-xl font-semibold transition focus:outline-none focus:ring-2 focus:ring-app-primary/30 disabled:cursor-not-allowed disabled:opacity-60';

// Size/context controls density, not action meaning.
const DEFAULT_SIZE = 'px-5 py-2.5 text-sm';
const COMPACT_SIZE = 'px-3 py-2 text-sm';
const ICON_SIZE = 'h-10 w-10';

// Intent controls visual meaning.
// Neutral/brand UI uses app tokens; operational semantics use universal red/green/amber.
const INTENT = {
  primary: 'border border-transparent bg-app-primary font-bold text-white shadow-sm hover:opacity-90',
  secondary: 'border border-app-border bg-app-card text-app-text hover:bg-app-surface',
  destructive: 'border border-red-200 bg-app-card text-red-700 hover:bg-red-50 focus:ring-red-200',
  success: 'border border-green-200 bg-app-card text-green-700 hover:bg-green-50 focus:ring-green-200',
  warning: 'border border-amber-200 bg-app-card text-amber-700 hover:bg-amber-50 focus:ring-amber-200',
  info: 'border border-blue-200 bg-app-card text-blue-700 hover:bg-blue-50 focus:ring-blue-200',
  ghost: 'border border-transparent bg-transparent text-app-text hover:bg-app-surface',
};

// Menu actions are rows inside dropdowns, not bordered button shells.
const MENU_BASE =
  'w-full px-4 py-3 text-left text-sm font-semibold transition focus:outline-none';

const buildActionClass = (intent, size) => `${BASE} ${size} ${INTENT[intent]}`;

export const COMPANY_ACTION_CLASSES = {
  // Default actions: page headers, modal/drawer footers, and larger controls.
  primary: buildActionClass('primary', DEFAULT_SIZE),
  secondary: buildActionClass('secondary', DEFAULT_SIZE),
  destructive: buildActionClass('destructive', DEFAULT_SIZE),
  success: buildActionClass('success', DEFAULT_SIZE),
  warning: buildActionClass('warning', DEFAULT_SIZE),
  ghost: buildActionClass('ghost', DEFAULT_SIZE),

  // Compact actions: table rows, mobile cards, and dense inline action groups.
  compactSecondary: buildActionClass('secondary', COMPACT_SIZE),
  compactDestructive: buildActionClass('destructive', COMPACT_SIZE),
  compactSuccess: buildActionClass('success', COMPACT_SIZE),
  compactWarning: buildActionClass('warning', COMPACT_SIZE),
  compactInfo: buildActionClass('info', COMPACT_SIZE),

  // Icon actions: fixed-square controls such as details, menu triggers, and compact row icons.
  iconSecondary: buildActionClass('secondary', ICON_SIZE),
  iconSuccess: buildActionClass('success', ICON_SIZE),
  iconDestructive: buildActionClass('destructive', ICON_SIZE),

  // Menu actions: full-width dropdown rows.
  menuItem: `${MENU_BASE} text-app-text hover:bg-app-surface`,
  menuDestructive: `${MENU_BASE} text-red-700 hover:bg-red-50`,
};
